import { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  api,
  OPS_NEXT,
  type PublishMode,
  type QuotaGauge,
  type Tenant,
  type WorkItem,
} from '../api';
import { ItemDetail } from './ReviewQueue';
import { useToast } from '../ui/Toast';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Badge, EmptyState, Field, LoadingPanel, PageHeader } from '../ui/bits';

export function Tenants() {
  return (
    <Routes>
      <Route index element={<TenantList />} />
      <Route path=":tenantId" element={<Client360 />} />
    </Routes>
  );
}

interface KpiRow {
  key: string;
  label: string;
  formula: string;
  value: number | null;
  byDim?: Record<string, number>;
}
interface KpiData {
  period: string;
  reportOrder: string[];
  kpis: KpiRow[];
  funnel: Array<{ stage: string; label: string; count: number }>;
}

const CENTS_KPIS = /^(cp|revenue|spend)/; // cost-per-*, revenue*, spend* are cents

function fmtKpi(row: KpiRow): string {
  if (row.value === null) return '—';
  if (CENTS_KPIS.test(row.key)) return `$${(row.value / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (row.formula.includes('/')) return `${(row.value * 100).toFixed(0)}%`;
  return row.value.toLocaleString();
}

function KpiPanel({ tenantId }: { tenantId: string }) {
  const [data, setData] = useState<KpiData | null>(null);
  const toast = useToast();
  const load = useCallback(
    () => api.get<KpiData>(`/api/admin/tenants/${tenantId}/kpis`).then(setData).catch(console.error),
    [tenantId]
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (!data) return <LoadingPanel label="Loading KPIs…" />;

  const ordered = [
    ...data.reportOrder.map((k) => data.kpis.find((x) => x.key === k)).filter((x): x is KpiRow => Boolean(x)),
    ...data.kpis.filter((k) => !data.reportOrder.includes(k.key)),
  ];
  return (
    <div className="panel">
      <h3 className="row-baseline">
        KPIs · {data.period}
        <button
          type="button"
          className="btn-sm"
          onClick={() =>
            api
              .post(`/api/admin/tenants/${tenantId}/rollup`)
              .then(() => toast.success('Recompute enqueued — refresh shortly.'))
              .catch(() => toast.error('Queue unavailable — recompute not enqueued.'))
          }
        >
          Recompute
        </button>
      </h3>
      <div className="row" style={{ gap: 16, marginBottom: 12 }}>
        {data.funnel.map((f) => (
          <div key={f.stage} className="stat" style={{ textAlign: 'center', minWidth: 0 }}>
            <div className="stat-num">{f.count}</div>
            <div className="stat-lbl">{f.label}</div>
          </div>
        ))}
      </div>
      <div className="row" style={{ gap: 24 }}>
        {ordered.map((k) => (
          <div key={k.key}>
            <strong>{fmtKpi(k)}</strong> <span className="stat-lbl">{k.label}</span>
            {k.byDim && Object.keys(k.byDim).length > 0 && (
              <div className="muted tiny">
                {Object.entries(k.byDim)
                  .map(([dim, v]) => `${dim}: $${(v / 100).toLocaleString()}`)
                  .join(' · ')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

interface ConnectedAccount {
  id: string;
  channel: string;
  status: string;
  externalId: string | null;
  lastHealthyAt: string | null;
}

const CHANNEL_LABELS: Record<string, string> = { gbp: 'Google Business Profile', meta: 'Meta (FB/IG)' };

function ConnectionsPanel({ tenantId }: { tenantId: string }) {
  const [accounts, setAccounts] = useState<ConnectedAccount[] | null>(null);
  useEffect(() => {
    api.get<ConnectedAccount[]>(`/api/admin/tenants/${tenantId}/connected-accounts`).then(setAccounts).catch(console.error);
  }, [tenantId]);
  const params = new URLSearchParams(window.location.search);
  const justConnected = params.get('connected');
  const connectError = params.get('connect_error');

  const gbp = accounts?.find((a) => a.channel === 'gbp');
  return (
    <div className="panel">
      <h3>Channel connections</h3>
      {justConnected && (
        <p className="callout callout--ok">Connected {CHANNEL_LABELS[justConnected] ?? justConnected} ✓</p>
      )}
      {connectError && (
        <p className="callout callout--danger" role="alert">
          Connection failed: {connectError}
        </p>
      )}
      {accounts === null ? (
        <LoadingPanel />
      ) : (
        <div className="row" style={{ gap: 16 }}>
          <span className="row-baseline" style={{ gap: 6 }}>
            <strong>{CHANNEL_LABELS.gbp}:</strong>
            {gbp ? (
              <>
                <Badge state={gbp.status} />
                {gbp.lastHealthyAt && (
                  <span className="muted small">healthy {new Date(gbp.lastHealthyAt).toLocaleString()}</span>
                )}
              </>
            ) : (
              <span className="muted">not connected</span>
            )}
          </span>
          <a className="btn" href={`/api/oauth/google/start?tenantId=${tenantId}&channel=gbp`}>
            {gbp ? 'Reconnect' : 'Connect Google Business Profile'}
          </a>
        </div>
      )}
    </div>
  );
}

interface ReportRow {
  id: string;
  period: string;
  state: string;
  pdfKey: string | null;
  deliveredAt: string | null;
  narrative?: { headline?: string };
}

function ReportsPanel({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<ReportRow[] | null>(null);
  const [confirmDeliver, setConfirmDeliver] = useState<ReportRow | null>(null);
  const toast = useToast();
  const load = useCallback(
    () => api.get<ReportRow[]>(`/api/admin/tenants/${tenantId}/reports`).then(setRows).catch(console.error),
    [tenantId]
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (rows === null) return <LoadingPanel label="Loading reports…" />;

  const generate = (period?: string) =>
    api
      .post(`/api/admin/tenants/${tenantId}/reports/generate`, period ? { period } : {})
      .then((r) => toast.success(`Generating ${(r as { period: string }).period} report — refresh in ~30s.`))
      .catch(() => toast.error('Queue unavailable — report not enqueued.'));
  const deliver = (id: string) =>
    api
      .post(`/api/admin/reports/${id}/deliver`, {})
      .then((r) => {
        const to = (r as { delivered_to: string[] }).delivered_to;
        if (to.length) toast.success(`Delivered to ${to.join(', ')}`);
        else toast.info('PDF stored; no recipients passed the send guard.');
        void load();
      })
      .catch(() => toast.error('Delivery failed.'));

  return (
    <div className="panel">
      <h3 className="row-baseline">
        Monthly reports
        <button type="button" className="btn-sm" onClick={() => void generate(new Date().toISOString().slice(0, 7))}>
          Generate (current month)
        </button>
        <button type="button" className="btn-sm" onClick={() => void generate()}>
          Generate (previous month)
        </button>
      </h3>
      {rows.length === 0 && <EmptyState title="No reports yet" hint="Reports auto-generate on the 1st of each month." />}
      {rows.map((r) => (
        <div key={r.id} className="row-baseline" style={{ gap: 10, marginBottom: 6 }}>
          <strong>{r.period}</strong>
          <Badge state={r.state} />
          {r.narrative?.headline && <span className="muted small">{r.narrative.headline}</span>}
          {r.pdfKey && (
            <a href={`/api/admin/report-pdf?key=${encodeURIComponent(r.pdfKey)}`} target="_blank" rel="noreferrer">
              PDF
            </a>
          )}
          {r.state !== 'delivered' && (
            <button type="button" className="btn-sm" onClick={() => setConfirmDeliver(r)}>
              Deliver to client
            </button>
          )}
        </div>
      ))}
      <ConfirmDialog
        open={confirmDeliver !== null}
        title={`Deliver the ${confirmDeliver?.period ?? ''} report?`}
        body="Emails the PDF to the client's recipients now and marks the report delivered."
        confirmLabel="Deliver report"
        onConfirm={() => {
          if (confirmDeliver) void deliver(confirmDeliver.id);
        }}
        onClose={() => setConfirmDeliver(null)}
      />
    </div>
  );
}

function TenantList() {
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get<Tenant[]>('/api/admin/tenants').then(setTenants).catch(console.error);
  }, []);
  return (
    <div className="page-stack">
      <PageHeader title="Client 360" sub={tenants ? `${tenants.length} clients` : undefined} />
      <div className="panel">
        {tenants === null && <LoadingPanel />}
        {tenants !== null && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Timezone</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr
                    key={t.id}
                    tabIndex={0}
                    className="clickable"
                    onClick={() => navigate(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(t.id);
                      }
                    }}
                  >
                    <td>
                      <strong>{t.displayName}</strong>
                    </td>
                    <td className="mono">{t.slug}</td>
                    <td>
                      <Badge state={t.status} />
                    </td>
                    <td className="muted small">{t.timezone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface TenantDetail {
  tenant: Tenant;
  entitlements: { planKey: string | null; planName: string | null; level: number | null };
  packKey: string;
  packName: string;
}

function Client360() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [gauges, setGauges] = useState<QuotaGauge[]>([]);
  const [modes, setModes] = useState<PublishMode[]>([]);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [selected, setSelected] = useState<WorkItem | null>(null);
  const [overridesText, setOverridesText] = useState('');
  const [overridesError, setOverridesError] = useState<string | null>(null);
  const [confirmAuto, setConfirmAuto] = useState<string | null>(null); // channel pending auto switch
  const toast = useToast();

  const load = useCallback(async () => {
    if (!tenantId) return;
    const [d, q, m, wi] = await Promise.all([
      api.get<TenantDetail>(`/api/admin/tenants/${tenantId}`),
      api.get<{ gauges: QuotaGauge[] }>(`/api/admin/tenants/${tenantId}/quotas`),
      api.get<PublishMode[]>(`/api/admin/tenants/${tenantId}/publish-modes`),
      api.get<WorkItem[]>(`/api/work-items?tenantId=${tenantId}`),
    ]);
    setDetail(d);
    setGauges(q.gauges);
    setModes(m);
    setItems(wi);
    setOverridesText(JSON.stringify(d.tenant.packOverrides, null, 2));
    setSelected(null);
  }, [tenantId]);
  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  if (!detail) return <LoadingPanel label="Loading client…" />;

  const saveOverrides = async () => {
    setOverridesError(null);
    try {
      const packOverrides = JSON.parse(overridesText);
      await api.put(`/api/admin/tenants/${tenantId}/pack-overrides`, { packOverrides });
      toast.success('Overrides saved.');
      void load();
    } catch (err) {
      setOverridesError(err instanceof SyntaxError ? 'Not valid JSON' : 'Rejected: resolved pack fails schema validation');
    }
  };

  const setMode = async (channel: string, mode: string) => {
    try {
      await api.put(`/api/admin/tenants/${tenantId}/publish-modes`, { channel, mode });
      toast.success(`${channel} publish mode → ${mode.replaceAll('_', ' ')}.`);
    } catch {
      toast.error(`Could not change the ${channel} publish mode.`);
    }
    void load();
  };

  const setCap = async (m: PublishMode, capText: string) => {
    const cap = capText.trim() === '' ? null : Number(capText);
    if (cap !== null && (!Number.isInteger(cap) || cap < 1)) {
      toast.error('Daily cap must be a whole number of 1 or more (or blank for uncapped).');
      return;
    }
    await api.put(`/api/admin/tenants/${tenantId}/publish-modes`, { channel: m.channel, mode: m.mode, dailyCap: cap });
    toast.success(`${m.channel} daily cap ${cap === null ? 'removed' : `set to ${cap}`}.`);
    void load();
  };

  const createItem = async (form: FormData) => {
    await api.post('/api/work-items', {
      tenantId,
      family: 'content',
      type: String(form.get('type')),
      channel: String(form.get('channel')) || undefined,
      payload: { body: String(form.get('body')) },
    });
    void load();
  };

  return (
    <div className="page-stack">
      <div className="panel">
        <PageHeader
          title={detail.tenant.displayName}
          sub={
            <>
              {detail.packName} pack · plan: {detail.entitlements.planName ?? 'none'}
            </>
          }
          actions={<Badge state={detail.tenant.status} />}
        />
        <div className="row" style={{ gap: 24, marginTop: 12 }}>
          {gauges.map((g) => (
            <div key={g.featureKey} className="stat">
              <div className="stat-num" style={{ color: g.warn ? 'var(--warn)' : undefined }}>
                {g.used}/{g.quota}
              </div>
              <div className="stat-lbl">
                {g.featureKey.replaceAll('_', ' ')} · {g.period}
              </div>
            </div>
          ))}
        </div>
      </div>

      <KpiPanel tenantId={tenantId!} />

      <ConnectionsPanel tenantId={tenantId!} />

      <ReportsPanel tenantId={tenantId!} />

      <div className="panel">
        <h3>Publish modes</h3>
        <div className="row-baseline" style={{ gap: 20 }}>
          {modes.map((m) => (
            <label key={m.channel} className="row-baseline" style={{ gap: 6 }}>
              {m.channel}
              <select
                value={m.mode}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next === 'auto') setConfirmAuto(m.channel);
                  else void setMode(m.channel, next);
                }}
              >
                <option value="manual">manual</option>
                <option value="client_review">client_review</option>
                <option value="auto">auto</option>
              </select>
              {m.mode === 'auto' && (
                <input
                  type="number"
                  min={1}
                  placeholder="cap/day"
                  aria-label={`${m.channel} daily auto-publish cap`}
                  title="Max auto-publishes per day on this channel (blank = uncapped)"
                  defaultValue={m.dailyCap ?? ''}
                  onBlur={(e) => void setCap(m, e.target.value)}
                  style={{ width: 74 }}
                />
              )}
            </label>
          ))}
        </div>
        <p className="muted small" style={{ margin: '8px 0 0' }}>
          auto = guardrails then publish at the slot, no human review. Anything a guardrail flags is held into the
          review queue. Pausing the tenant stops all auto-publishing instantly.
        </p>
        <div style={{ marginTop: 12, maxWidth: 520 }}>
          <Field
            label="Email campaign recipients"
            hint="Client-provided, comma-separated — where the email channel publishes."
          >
            {(id) => (
              <input
                id={id}
                defaultValue={((detail.tenant.businessProfile?.campaign_recipients as string[]) ?? []).join(', ')}
                placeholder="office@client.com, list-import@client-tool.com"
                onBlur={(e) => {
                  const campaign_recipients = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
                  void api
                    .put(`/api/admin/tenants/${tenantId}/business-profile`, {
                      patch: { campaign_recipients: campaign_recipients.length > 0 ? campaign_recipients : null },
                    })
                    .then(() => {
                      toast.success('Campaign recipients saved.');
                      return load();
                    })
                    .catch(() => toast.error('Could not save recipients.'));
                }}
              />
            )}
          </Field>
        </div>
      </div>

      <div className="grid-2">
        <div className="panel">
          <h3>Work items</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void createItem(new FormData(e.currentTarget));
              e.currentTarget.reset();
            }}
            className="row"
            style={{ marginBottom: 12 }}
          >
            <select name="type" defaultValue="social_post" aria-label="Item type">
              <option value="social_post">social post</option>
              <option value="gbp_post">GBP post</option>
              <option value="seo_article">SEO article</option>
              <option value="email_campaign">email campaign</option>
            </select>
            <select name="channel" defaultValue="gbp" aria-label="Channel">
              <option value="gbp">gbp</option>
              <option value="meta">meta</option>
              <option value="">none</option>
            </select>
            <input name="body" placeholder="draft copy" aria-label="Draft copy" style={{ flex: 1, minWidth: 140 }} />
            <button className="primary" type="submit">
              New item
            </button>
          </form>
          {items.length === 0 ? (
            <EmptyState title="No work items yet" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Channel</th>
                    <th>State</th>
                    <th>Next</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((wi) => (
                    <tr
                      key={wi.id}
                      tabIndex={0}
                      aria-selected={selected?.id === wi.id}
                      className={`clickable${selected?.id === wi.id ? ' selected' : ''}`}
                      onClick={() => setSelected(wi)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelected(wi);
                        }
                      }}
                    >
                      <td>{wi.type.replaceAll('_', ' ')}</td>
                      <td>{wi.channel ?? '—'}</td>
                      <td>
                        <Badge state={wi.state} />
                      </td>
                      <td className="muted tiny">
                        {(OPS_NEXT[wi.state] ?? []).map((a) => a.label).join(' / ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {selected ? (
          <ItemDetail key={selected.id} item={selected} onChanged={() => void load()} />
        ) : (
          <div className="panel">
            <EmptyState title="Select a work item" hint="Click a row (or focus it and press Enter)." />
          </div>
        )}
      </div>

      <div className="panel">
        <h3>Pack overrides (JSON — deep-merged over the pack, tenant wins)</h3>
        <textarea
          value={overridesText}
          onChange={(e) => setOverridesText(e.target.value)}
          rows={8}
          className="mono"
          aria-label="Pack overrides (JSON)"
          style={{ width: '100%' }}
        />
        {overridesError && (
          <p className="callout callout--danger" role="alert" style={{ marginTop: 8 }}>
            {overridesError}
          </p>
        )}
        <div style={{ marginTop: 8 }}>
          <button type="button" className="primary" onClick={saveOverrides}>
            Save overrides
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAuto !== null}
        danger
        title={`Turn on auto-publish for ${confirmAuto ?? ''}?`}
        body="Guardrail-checked content publishes with no human review. Anything a guardrail flags is held into the review queue instead."
        confirmLabel="Enable auto"
        onConfirm={() => {
          if (confirmAuto) void setMode(confirmAuto, 'auto');
        }}
        onClose={() => setConfirmAuto(null)}
      />
    </div>
  );
}
